import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { withBranch } from '../api/branch';
import { apiFetch } from '../api/client';
import type { SearchIndex } from '../api/types';
import { useViewBranch } from '../view/ViewBranchContext';

import { rowDomId } from './rowId';

type SearchHit = {
  groupId: string;
  groupName: string;
  key: string;
  /** 各 locale ごとの value。マッチした locale だけでなく全て持たせ、UI 側で並べる。 */
  values: { locale: string; value: string }[];
  /** マッチ箇所のメタ: key にマッチしたか、value にマッチした locale */
  matchedInKey: boolean;
  matchedLocales: Set<string>;
};

// 結果が多すぎると DOM が重くなるので上限。100 件あれば検索ワードを絞り込めば十分という想定。
const MAX_HITS = 100;

const normalize = (s: string): string => s.toLowerCase().normalize('NFKC');

const Highlight = ({ text, query }: { text: string; query: string }) => {
  if (query === '') return <>{text}</>;
  const haystack = normalize(text);
  const needle = normalize(query);
  // index は normalize 後の文字列上のものだが、NFKC 後も多くの場合は文字数が変わらない。
  // ここでは表示用ハイライトの近似で十分なので元文字列の同 index を取り出している。
  // 長さは normalize 後の needle 基準にする (idx と同じ座標系のため、合字展開等で
  // query.length と needle.length が乖離するケースでも idx + 長さ の整合が崩れにくい)。
  const idx = haystack.indexOf(needle);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded bg-yellow-200 px-0.5">{text.slice(idx, idx + needle.length)}</mark>
      {text.slice(idx + needle.length)}
    </>
  );
};

type Props = {
  onClose: () => void;
};

// このダイアログは open 時のみ mount される (AppLayout 側で条件描画)。閉じる = unmount
// なので検索ワード等の state は自動的に破棄され、明示的なリセットは不要。
export const SearchDialog = ({ onClose }: Props) => {
  const navigate = useNavigate();
  const { viewBranch } = useViewBranch();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['search-index', viewBranch],
    queryFn: () => apiFetch<SearchIndex>(withBranch('/api/files/search-index', viewBranch)),
    // 全 group 全 locale を 1 度に読むのは GitHub API への負荷が高い。明示再取得まで使い回す。
    // (キャッシュは QueryClient 側なので mount/unmount を跨いで再利用される)
    staleTime: Infinity,
  });

  // mount 時 = 開いた瞬間に input にフォーカス。
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const hits = useMemo<SearchHit[]>(() => {
    if (data === undefined) return [];
    const trimmed = query.trim();
    if (trimmed === '') return [];
    const needle = normalize(trimmed);
    const results: SearchHit[] = [];
    for (const g of data.groups) {
      // key の集合を全 locale の union で作る (locale ごとに欠けがあるため)
      const keys = new Set<string>();
      for (const l of g.locales) for (const k of Object.keys(l.entries)) keys.add(k);
      for (const k of Array.from(keys).sort()) {
        const matchedInKey = normalize(k).includes(needle);
        const matchedLocales = new Set<string>();
        const values: { locale: string; value: string }[] = [];
        for (const l of g.locales) {
          const v = l.entries[k] ?? '';
          values.push({ locale: l.locale, value: v });
          if (v !== '' && normalize(v).includes(needle)) matchedLocales.add(l.locale);
        }
        if (!matchedInKey && matchedLocales.size === 0) continue;
        results.push({
          groupId: g.id,
          groupName: g.name,
          key: k,
          values,
          matchedInKey,
          matchedLocales,
        });
        if (results.length >= MAX_HITS) return results;
      }
    }
    return results;
  }, [data, query]);

  const go = (groupId: string, key: string) => {
    onClose();
    void navigate(`/groups/${groupId}#${rowDomId(key)}`);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 px-4 pt-[10vh]"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="key・日本語・各言語を横断検索"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
          />
          {query.trim() !== '' && !isLoading && error === null && (
            <span className="text-[11px] text-gray-500" aria-live="polite">
              {hits.length >= MAX_HITS ? `${MAX_HITS}+ 件` : `${hits.length} 件`}
            </span>
          )}
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="rounded border px-2 py-0.5 text-[10px] text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            title="検索インデックスを再取得"
          >
            {isFetching ? '更新中' : '再取得'}
          </button>
          <kbd className="rounded border px-1.5 py-0.5 text-[10px] text-gray-500">ESC</kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading && (
            <div className="px-4 py-6 text-sm text-gray-500">検索インデックス読込中...</div>
          )}
          {error !== null && <div className="px-4 py-6 text-sm text-red-700">{error.message}</div>}
          {!isLoading && error === null && query.trim() === '' && (
            <div className="px-4 py-6 text-sm text-gray-500">
              key や訳文 (日本語 / English / 한국어 / 中文 ...) を入力してください
            </div>
          )}
          {!isLoading && error === null && query.trim() !== '' && hits.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-500">該当なし</div>
          )}
          {hits.length > 0 && (
            <ul className="divide-y">
              {hits.map((h) => (
                <li key={`${h.groupId}::${h.key}`}>
                  <button
                    type="button"
                    onClick={() => go(h.groupId, h.key)}
                    className="block w-full px-4 py-2 text-left hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between gap-2 text-[10px] text-gray-500">
                      <span>{h.groupName}</span>
                      {h.matchedInKey && (
                        <span className="rounded bg-blue-100 px-1 text-blue-700">key match</span>
                      )}
                    </div>
                    <div className="mt-0.5 font-mono text-xs break-all text-gray-800">
                      <Highlight text={h.key} query={query} />
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {h.values
                        .filter((v) => v.value !== '')
                        .map((v) => {
                          const matched = h.matchedLocales.has(v.locale);
                          return (
                            <div
                              key={v.locale}
                              className={`flex gap-2 text-xs ${matched ? 'text-gray-900' : 'text-gray-400'}`}
                            >
                              <span className="w-12 shrink-0 font-mono text-[10px]">
                                {v.locale}
                              </span>
                              <span className="min-w-0 flex-1 truncate">
                                {matched ? <Highlight text={v.value} query={query} /> : v.value}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {hits.length >= MAX_HITS && (
            <div className="border-t px-4 py-2 text-[10px] text-gray-500">
              上位 {MAX_HITS} 件のみ表示中。検索ワードを絞ってください。
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
